import { Component, Input, OnChanges, ViewChild, ElementRef, SimpleChanges, forwardRef } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { BaseFormWidgetComponent } from "../../../shared/base-form.component";

@Component({
    selector: 'time-input',
    template: `<div [class]="'time-input' + (klass ? ' ' + klass : '') + (disabled ? ' disable' : '')" widget>
    <div class="hour">
        <div class="arrow top left" (touchrelease)="changeHour(1)"></div>
        <input #hour [(ngModel)]="data.hour" (ngModelChange)="checkHour()" type="tel" maxlength="2" placeholder="hh"
            [class.error]="data.hour_error" (keydown.;)="nextField($event)" (keyup.arrowright)="nextField($event)"
            (keyup.arrowup)="changeHour(1)" (keyup.arrowdown)="changeHour(-1)">

        <div class="arrow bottom left" (touchrelease)="changeHour(-1)"></div>
    </div>
    <div class="divider">:</div>
    <div class="minute">
        <div class="arrow top right" (touchrelease)="changeMinute(1)"></div>
        <input #minute [(ngModel)]="data.minute" (ngModelChange)="checkMinute()" type="tel" maxlength="2" placeholder="mm"
            [class.error]="data.minute_error" (keyup.colon)="nextField($event)" (keyup.arrowleft)="previousField($event)"
            (keyup.backspace)="previousField($event)" (keyup.arrowup)="changeMinute(1)" (keyup.arrowdown)="changeMinute(-1)">
        <div class="arrow bottom right" (touchrelease)="changeMinute(-1)"></div>
    </div>
    <div class="period" (touchrelease)="data.pm = !data.pm; post()">
        {{ data.pm ? 'PM' : 'AM' }}
    </div>
</div>
`,
    styles: [`.time-input{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;border-radius:4px;margin:1em .5em}.time-input.disable{opacity:.2;pointer-events:none}input{background:0 0;width:1.5em;padding:0;outline:0;border:none;border-radius:0;border-bottom:1px solid transparent}input.error{border-bottom:1px solid #f44336;color:rgba(0,0,0,.87)}.hour{position:relative;margin-left:.5em;width:1.5em}.hour input{text-align:right}.minute{position:relative;width:1.5em}.arrow{position:absolute;left:50%;height:1.5em;width:1.5em;background-position:center;background-size:contain;background-repeat:no-repeat}.arrow.top{top:0;-webkit-transform:translate(-50%,-120%);transform:translate(-50%,-120%);background-image:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNNy40MSAxNS40MUwxMiAxMC44M2w0LjU5IDQuNThMMTggMTRsLTYtNi02IDZ6Ii8+PHBhdGggZD0iTTAgMGgyNHYyNEgweiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==)}.arrow.bottom{bottom:0;-webkit-transform:translate(-50%,120%);transform:translate(-50%,120%);background-image:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNNy40MSA4LjU5TDEyIDEzLjE3bDQuNTktNC41OEwxOCAxMGwtNiA2LTYtNiAxLjQxLTEuNDF6Ii8+PHBhdGggZmlsbD0ibm9uZSIgZD0iTTAgMGgyNHYyNEgwVjB6Ii8+PC9zdmc+)}.arrow.left{left:calc(50% + .25em)}.arrow.right{left:calc(50% - .25em)}.divider{padding:0 .2em;margin-bottom:2px}.period{padding:.5em;cursor:pointer;border-radius:2px;-webkit-box-shadow:0 1px 3px 0 transparent,0 1px 1px 0 transparent,0 2px 1px -1px transparent;box-shadow:0 1px 3px 0 transparent,0 1px 1px 0 transparent,0 2px 1px -1px transparent;background-color:rgba(240,240,240,0);-webkit-transition:background-color .2s,-webkit-box-shadow .2s;transition:box-shadow .2s,background-color .2s,-webkit-box-shadow .2s}.period:hover{-webkit-box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);background-color:#f0f0f0}.period:active{-webkit-box-shadow:0 1px 3px 0 transparent,0 1px 1px 0 transparent,0 2px 1px -1px transparent;box-shadow:0 1px 3px 0 transparent,0 1px 1px 0 transparent,0 2px 1px -1px transparent}`],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TimeInputComponent),
        multi: true
    }]
})
export class TimeInputComponent extends BaseFormWidgetComponent<string> implements OnChanges, ControlValueAccessor {
    @Input('no-period') public no_period: boolean;
    @Input() public arrows: boolean = true;
    @Input() public step: number = 5;

    public data: { [name: string]: any } = { hour: '12', minute: '00' };

    @ViewChild('hour') public hour_field: ElementRef;
    @ViewChild('minute') public minute_field: ElementRef;

    public ngOnInit() {
        this.data.last_location = 0;
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.model && changes.model.previousValue !== this.model) {
            this.setData(this.model);
        }
    }

    public writeValue(value) {
        this.model = value;
        this.setData(this.model);
    }

    public setData(time) {
        const parts = (time || '00:00').split(':');
        this.data.hour = 0;
        this.data.pm = +parts[0] >= 12;
        this.changeHour(+parts[0] % 12, false);
        this.data.minute = 0;
        const minute = Math.ceil(+parts[1] / this.step) % Math.floor(60 / this.step);
        this.changeMinute(minute, false);
    }

    public nextField(e) {
        if (e && e.preventDefault) { e.preventDefault(); }
        if (this.hour_field && this.minute_field) {
            const current = this.hour_field.nativeElement;
            const other = e.key.toLowerCase() !== 'semicolon' && this.data.last_location === (current.value || '').length;
            const right_arrow = e.key.toLowerCase() === 'arrowright' && this.data.last_location === (current.value || '').length;
            if ((other || right_arrow)) {
                const el = this.minute_field.nativeElement;
                el.focus();
                el.selectionStart = el.selectionEnd = 0;
            }
            this.data.last_location = current.selectionEnd;
        }
    }

    public previousField(e) {
        if (e && e.preventDefault) { e.preventDefault(); }
        if (this.hour_field && this.minute_field) {
            const current = this.minute_field;
            const backspace = e.key.toLowerCase() === 'backspace' && this.data.last_location === 0;
            const left_arrow = e.key.toLowerCase() === 'arrowleft' && this.data.last_location === 0;
            if ((backspace || left_arrow) && current.nativeElement.selectionEnd === 0) {
                const el = this.hour_field.nativeElement;
                el.focus();
                el.selectionStart = el.selectionEnd = (el.value || '').length;
            }
            this.data.last_location = current.nativeElement.selectionEnd;
        }
    }

    public checkHour() {
        this.data.hour_error = false;
        if (!this.data.hour) { return true; }
        try {
            JSON.parse(`{ "test": ${this.data.hour} }`);
        } catch (e) {
            this.data.hour_error = true;
            return false;
        }
        if ((+this.data.hour > 12 && !this.no_period) || +this.data.hour > 24 || +this.data.hour < 0) {
            this.data.hour_error = true;
            return false;
        }
        return true;
    }

    public checkMinute() {
        this.data.minute_error = false;
        if (!this.data.minute || this.data.minute === '00') { return true; }
        try {
            const time = this.data.minute.length >= 2 && this.data.minute[0] === '0' ? this.data.minute[1] : this.data.minute;
            JSON.parse(`{ "test": ${time || '""'} }`);
        } catch (e) {
            this.data.minute_error = true;
            return false;
        }
        if (+this.data.minute >= 60) {
            this.data.minute_error = true;
            return false;
        }
        return true;
    }

    public changeHour(value: number, post: boolean = true) {
        if (!this.data.hour || !this.checkHour()) { this.data.hour = '0'; }
        this.data.hour = `${(+this.data.hour % 12) + value || 0}`;
        if (+this.data.hour < 0) {
            this.data.hour = 12 + +this.data.hour % 12;
            this.data.pm = !this.data.pm;
        }
        const hour = (+this.data.hour) + (this.data.pm ? 12 : 0);
            // Check the period of the day
        this.data.pm = hour % 24 >= 12;
        if (!this.no_period) {
            this.data.hour = `${+this.data.hour % 12}`;
        }
            // If hour is zero set it to 12
        if (this.data.hour === '0') { this.data.hour = '12'; }
        if (post) { this.post(); }
    }

    public changeMinute(value: number, post: boolean = true) {
        if (!this.data.minute || !this.checkMinute()) { this.data.minute = '00'; }
        this.data.minute = `${parseInt(this.data.minute) + ((value || 0) * this.step)}`;
        if (+this.data.minute >= 60) {
            this.changeHour(1, post);
        } else if (+this.data.minute < 0) {
            this.changeHour(-1, post);
        }
        this.data.minute = `${+this.data.minute < 0 ? 60 + +this.data.minute : +this.data.minute % 60}`;
        if (+this.data.minute < 10) { this.data.minute = '0' + this.data.minute; }
        if (post) { this.post(); }
    }

    public post() {
        this.timeout('post', () => {
            let hour: number | string = ((+this.data.hour || 0) % 12) + (this.data.pm ? 12 : 0);
            if (+hour < 10) { hour = '0' + hour; }
            const minute = +this.data.minute < 10 ? '0' + +this.data.minute : this.data.minute;
            this.modelChange.emit(`${hour}:${minute}`);
            this.data.post_timer = null;
        }, 50);
    }

}
